import { useGetIndustryList } from "@/api/services/global/industry";
import { InnerBox } from "@/components/elements/InnerBox";
import { CompanyFormTitle } from "@/features/company/components/company-info/CompanyFormTitle";
import { text } from "@/theme/colors";
import { spacing } from "@/theme/variables";
import { Stack, Typography } from "@mui/material";
import { TRsoForm } from "@/features/rso/types/rsoForm";
import { useFormContext, useWatch } from "react-hook-form";
import { Switch } from "@/components/elements/Switch";
import { FC, useCallback } from "react";

/**
 * 取り扱う分野 — RSO toggles which 分野 (industry) it handles. Stored as `hiddenIndustryIds`
 * (ids turned OFF); empty means every industry is enabled. Independent of company hiddenIndustryIds.
 */
export const RsoIndustrySection: FC = () => {
  const { control, setValue, getValues } = useFormContext<TRsoForm>();
  const hiddenIndustryIds = useWatch({ control, name: "hiddenIndustryIds" }) ?? [];

  const { data: publishedIndustries } = useGetIndustryList();

  const handleToggle = useCallback(
    (industryId: number, checked: boolean) => {
      const current = getValues("hiddenIndustryIds") ?? [];
      if (checked) {
        setValue("hiddenIndustryIds", current.filter((id) => Number(id) !== Number(industryId)), { shouldDirty: true });
      } else {
        setValue("hiddenIndustryIds", [...current, Number(industryId)], { shouldDirty: true });
      }
    },
    [getValues, setValue],
  );

  return (
    <InnerBox title={<CompanyFormTitle text="取り扱う分野" />} showDivider={false}>
      <Stack gap={3}>
        <Typography variant="cap12" color={text.primary}>
          手続きおよび学習進捗を管理・閲覧できる分野を選択ください。
        </Typography>
        <Stack gap={spacing.md}>
          {publishedIndustries.map((industry) => {
            const isEnabled = !hiddenIndustryIds.some((id) => Number(id) === Number(industry.id));
            // Cannot disable an industry that has registered learners (外国人).
            const isDisabled = isEnabled && !!industry.hasLearner;
            return (
              <Stack key={industry.id} gap={1}>
                <Stack flexDirection="row" alignItems="center" gap="14px">
                  <Stack sx={{ width: "fit-content", ml: "11px" }}>
                    <Switch
                      nonControl
                      checked={isEnabled}
                      disabled={isDisabled}
                      onChange={(_, checked) => handleToggle(industry.id, checked)}
                    />
                  </Stack>
                  <Typography variant="body14" color={text.primary}>
                    {industry.name}
                  </Typography>
                </Stack>
                {isDisabled && (
                  <Typography variant="cap12" color={text.tertiary}>
                    この分野に登録された外国人が存在するため、無効にできません。
                  </Typography>
                )}
              </Stack>
            );
          })}
        </Stack>
      </Stack>
    </InnerBox>
  );
};
